import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { format, parseISO, parse } from 'date-fns';
import { isEmpty, includes, isArray } from 'lodash';

import { Report, UnidataTable, Modal } from '~/components';
import { RadioGroup, Autocomplete, DateTime, Input } from '~/components/Form';
import { Select } from '~/components/Form/v2';
import { recordFiltering } from '~/store/modules/descargaDetalhe/actions';
import { number, t } from '~/helpers';
import api from '~/services/api';

import RecordDetails from './RecordDetails';
import * as S from './styles';

export default function DlgDescargaDetalhada() {
  const [hidden, setHidden] = useState(true);
  const [grid, setGrid] = useState(false);
  const [query, setQuery] = useState({});
  const [tank, setTank] = useState({});
  const [shift, setShift] = useState({ initial: [], final: [] });
  const [rowQuery, setRowQuery] = useState({
    issuance_date: '',
    period: '',
    emitter: '',
    station: '',
    type: '',
  });

  const modalRef = useRef(null);
  const formRef = useRef(null);
  const dataRedux = useSelector((state) => state.descargaDetalhe);
  const { profile } = useSelector((state) => state.user);

  const titlePage = useMemo(() => 'Relatório de Recebimentos', []);

  const optionsTypeRadio = useMemo(
    () => [
      { label: 'PDF', value: 'pdf' },
      { label: 'Excel', value: 'xls' },
      { label: 'Tela', value: 'grid' },
    ],
    []
  );

  const firstJobOptions = useMemo(
    () => [
      {
        post_desc: 'Todos',
        post_cod_posto: '',
      },
    ],
    []
  );

  const headerUnidataTable = useMemo(
    () => [
      { id: 'post_desc', label: 'Posto' },
      { id: 'desc_vol_tot_abast', label: 'Vol. Total Abast.', align: 'center' },
      {
        id: 'desc_datahora',
        label: 'Data',
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy'),
      },
      {
        id: 'desc_datahora',
        label: 'Hora',
        formatted: (value) => format(parseISO(value), 'hh:mm'),
      },
      { id: 'nf', label: 'DNF NF' },
      { id: 'rvt', label: 'DNF RVT' },
      { id: 'desc_veic_doc', label: 'DNF CT/VT' },
      { id: 'desc_num_pedido', label: 'DNF Pedido' },
      {
        id: 'desc_dt_emissao_nf',
        label: 'DNF Data Emissão NF',
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy'),
      },
      {
        id: 'desc_vol_temp_car_doc',
        label: 'DNF Manifestado',
        align: 'right',
        formatted: (value) => number(value, 1),
      },
      {
        id: 'desc_vol_20_doc',
        label: 'DNF Vol. 20',
        align: 'right',
        formatted: (value) => number(value, 1),
      },
      {
        id: 'desc_temp_car_doc',
        label: 'DNF Temp',
        align: 'right',
        formatted: (value) => number(value, 1),
      },
      { id: 'desc_dens_20_doc', label: 'DNF Dens. 20', align: 'right' },
      {
        id: 'desc_vol_tot_sonda',
        label: 'DR Vol. Total Sonda',
        align: 'center',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'desc_vol_tot_skid',
        label: 'DR Vol. Total SKID',
        align: 'center',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'desc_vol_amb_total',
        label: 'DR Vol. Amb',
        align: 'right',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'desc_vol_20_total',
        label: 'DR Vol. 20',
        align: 'right',
        formatted: (value) => number(value, 1),
      },
      {
        id: 'dif_amb',
        label: 'Dif. NF/REC Amb',
        formatted: (value) => number(value, 1),
      },
      {
        id: 'dif_nf_sonda',
        label: 'Dif. NF/SONDA Amb',
        formatted: (value) => number(value, 1),
      },
      {
        id: 'dif_nf_skid',
        label: 'Dif. NF/SKID Amb',
        formatted: (value) => number(value, 1),
      },
      { id: 'oper_cod_oper', label: 'Ope' },
    ],
    []
  );

  const handleModifyReportData = useCallback(
    (data) => {
      const {
        tipo,
        datahorafinal,
        datahorainicial,
        desc_veic_doc,
        post_cod_posto,
        post_desc,
        dataIni,
        dataFim,
      } = data;

      if (tipo === 'grid') {
        const ignoreList = [
          'datahorainicial',
          'datahorafinal',
          'tipo',
          'post_desc',
          'dataIni',
          'dataFim',
        ];
        const dataQuery = {
          wherefield: ['datahorafinal', 'datahorainicial'],
          valuewherefield: [
            dataFim || datahorafinal,
            dataIni || datahorainicial,
          ],
          typewherefield: ['MENOR_IGUAL', 'MAIOR_IGUAL'],
        };

        if (!post_cod_posto) ignoreList.push('post_cod_posto');

        Object.keys(JSON.parse(JSON.stringify(data)))
          .filter((form) => !includes(ignoreList, form))
          .forEach((form) => {
            dataQuery.wherefield.push(form);
            dataQuery.valuewherefield.push(data[form]);
            dataQuery.typewherefield.push('EQUALS');
          });

        setHidden(false);
        setGrid(true);
        setQuery(dataQuery);
        setRowQuery({
          issuance_date: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
          period: `${dataIni || datahorainicial} até ${
            dataFim || datahorafinal
          }`,
          emitter: profile.name,
          station: post_desc || 'Todos',
          type: 'Geral',
        });

        return undefined;
      }

      return {
        idtarefapath: 'dlgDescargaDetalhada.swf',

        usuario: profile.username,
        strusuario: profile.name,
        tipo,
        datainicial: dataIni || datahorainicial,
        datafinal: dataFim || datahorafinal,
        codposto: post_cod_posto || '',
        strposto: post_desc || 'Todos',
        noctvt: desc_veic_doc || '',
        tiporel: 'geral',
      };
    },
    [profile]
  );

  const openModalWithRecordDetails = useCallback((data, heading) => {
    if (!isEmpty(data))
      modalRef.current.handleOpenModal({ data, query: heading });
  }, []);

  const handleGetShift = useCallback(
    async (value, position) => {
      const dateTime = format(
        parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()),
        'dd/MM/yyyy 00:00:00'
      );
      try {
        const URL = position === 'initial' ? 'getturnosinicio' : 'getturnosfim';
        const query =
          position === 'initial'
            ? { datahorainicio: dateTime }
            : { datahorafinal: dateTime };

        const { data } = await api.get(`/turno/${URL}`, {
          params: {
            ...query,
            codigoposto: tank.post_cod_posto,
            pesquisalista: true,
          },
        });

        setShift((state) => ({
          ...state,
          [position]: [
            {
              label: 'Nenhum',
              turn_datahora_ini: '',
              turn_datahora_fim: '',
            },
            ...data,
          ].map((item) => ({
            ...item,
            label:
              item.label ||
              `${item.turn_num_turn} - ${
                position === 'initial' ? item.hora_ini : item.hora_fim
              }`,
            value:
              position === 'initial'
                ? item.turn_datahora_ini
                : item.turn_datahora_fim,
          })),
        }));
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [tank]
  );

  const handleGetChangeTank = useCallback((data) => {
    setTank(data);
    setShift({ initial: [], final: [] });

    formRef.current.handleClear('dataIni');
    formRef.current.handleClear('dataFim');
  }, []);

  // GRID
  if (grid && !isEmpty(query) && !isEmpty(rowQuery.period))
    return (
      <>
        <S.HeaderWrapper>
          <li>
            <span>Data Emissão:</span>
            {rowQuery.issuance_date}
          </li>
          <li>
            <span>Período:</span> {rowQuery.period}
          </li>
          <li>
            <span>Emissor:</span>
            {rowQuery.emitter}
          </li>
          <li>
            <span>Posto:</span>
            {rowQuery.station}
          </li>
          <li>
            <span>Tipo:</span>
            {rowQuery.type}
          </li>
        </S.HeaderWrapper>

        <UnidataTable
          fixed
          header={headerUnidataTable}
          read={recordFiltering}
          redux={dataRedux}
          title={titlePage}
          query={query}
          onRow={(value) => openModalWithRecordDetails(value, rowQuery)}
          keys={['desc_num_doc']}
          detach="desc_num_doc"
          maxHeight="calc(100vh - 36rem)"
          create={false}
          update={false}
          delete={false}
        />

        <Modal
          disableBackdropClick
          disableEscapeKeyDown
          form
          ref={modalRef}
          title={`${titlePage} - Detalhe`}
          component={RecordDetails}
          labelConfirm="Imprimir"
          labelCancell="Fechar"
        />
      </>
    );

  return (
    <Report
      ref={formRef}
      open={hidden}
      title={titlePage}
      relatorio="DESCARGASII"
      onClose={() => setHidden(false)}
      serialize={handleModifyReportData}
      initials={{
        tipo: 'grid',
        datahorainicial: format(new Date(), 'dd/MM/yyyy 00:00:00'),
        datahorafinal: format(new Date(), 'dd/MM/yyyy 23:59:59'),
      }}
    >
      <Autocomplete
        name="post_cod_posto"
        label="post_desc"
        url="usuarioposto"
        unique="post_cod_posto"
        title="Posto"
        first={firstJobOptions}
        hiddens={[{ name: 'post_desc' }]}
        getValue={(value) => {
          handleGetChangeTank(value);
        }}
      />

      <S.Columns grid={2}>
        <DateTime
          time
          name="datahorainicial"
          label="Data/Hora inicial"
          getValue={(value) => {
            handleGetShift(value, 'initial');
            formRef.current.handleClear('dataIni');
          }}
        />

        <Select name="dataIni" label="Turno" data={shift.initial} />
      </S.Columns>

      <S.Columns grid={2}>
        <DateTime
          time
          name="datahorafinal"
          label="Data/Hora final"
          getValue={(value) => {
            handleGetShift(value, 'final');
            formRef.current.handleClear('dataFim');
          }}
        />

        <Select name="dataFim" label="Turno" data={shift.final} />
      </S.Columns>

      <Input name="desc_veic_doc" label="Nº CT/VT" type="number" />

      <RadioGroup row name="tipo" label="Formato" options={optionsTypeRadio} />
    </Report>
  );
}
